/**
 * La vista de mes (spec 015 · FR-009).
 *
 * El arrastre es por puntero, no HTML5: en un chip de diez píxeles el DnD del
 * navegador no llega a empezar. Si hay más citas de las que caben, el primer
 * clic amplía; el segundo entra en el día.
 */
import { useRef, useState, type PointerEvent } from 'react'

import { leerFormaMes, recordarFormaMes, type FormaMes } from '../lib/calendario-memoria'
import { cn } from '../lib/cn'
import { mesDe, mismoMes } from './calendario-geometria'
import { DetalleCita } from './calendario-detalle'
import { MenuSoltarMes } from './calendario-mes-menu'
import { DialogoMoverCita } from './calendario-mover-dialogo'
import { columnasMesQueSeVen, filasMesQueSeVen } from './calendario-cerrados'
import { clicCeldaMes, propuestaAlCambiarDeDia, type Propuesta } from './calendario-propuestas'
import { rotuloCliente, type CitaCalendario, type PayloadDnD } from './calendario-rejilla'

const VISIBLES_POR_DIA = 3
const UMBRAL_ARRASTRE = 6

const DIAS_CABECERA = ['Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb', 'Dom']
const GRID_COLS = [
    '',
    'grid-cols-1',
    'grid-cols-2',
    'grid-cols-3',
    'grid-cols-4',
    'grid-cols-5',
    'grid-cols-6',
    'grid-cols-7',
] as const

type CitaMes = CitaCalendario & { dia: string }

/** Días de la hoja que tienen al menos una cita: la lista no enseña huecos. */
export function diasDeListaMes(celdas: string[], porDia: Map<string, readonly unknown[]>): string[] {
    return celdas.filter((d) => (porDia.get(d)?.length ?? 0) > 0)
}

function rotuloDiaLista(d: string): string {
    return new Date(`${d}T12:00:00Z`).toLocaleDateString('es-ES', {
        weekday: 'long',
        day: 'numeric',
        month: 'long',
    })
}

export function superaUmbralArrastre(dx: number, dy: number, umbral = UMBRAL_ARRASTRE): boolean {
    return Math.hypot(dx, dy) >= umbral
}

export function diaBajoPuntero(x: number, y: number): string | null {
    const el = document.elementFromPoint(x, y)
    const celda = el?.closest('[data-dia]')
    return celda instanceof HTMLElement ? celda.dataset['dia'] ?? null : null
}

function payloadDe(c: CitaMes): PayloadDnD {
    return {
        id: c.id,
        minutos: c.minutos,
        inicio: c.inicio,
        profesionalId: c.profesionalId,
        dia: c.dia,
    }
}

export function CalendarioMes({
    accountId,
    dia,
    citas,
    nombreServ,
    nombreProf,
    colorDe,
    alElegirDia,
    alProponer,
    diaCerrado,
    ocultarCerrados = false,
}: {
    accountId: string
    dia: string
    citas: CitaMes[]
    nombreServ: (id: number | null) => string
    nombreProf: (id: number | null) => string
    colorDe: (id: number | null) => string
    alElegirDia: (dia: string) => void
    alProponer?: ((p: Propuesta) => void) | undefined
    diaCerrado?: ((d: string) => boolean) | undefined
    ocultarCerrados?: boolean | undefined
}) {
    const celdas = mesDe(dia)
    const hoy = new Date().toISOString().slice(0, 10)
    const [detalle, setDetalle] = useState<CitaMes | null>(null)
    const [expandido, setExpandido] = useState<string | null>(null)
    const [sobre, setSobre] = useState<string | null>(null)
    const [arrastre, setArrastre] = useState<{ cita: CitaMes; x: number; y: number } | null>(null)
    const [menu, setMenu] = useState<{
        payload: PayloadDnD
        destino: string
        x: number
        y: number
    } | null>(null)
    const [moverHuecos, setMoverHuecos] = useState<{
        payload: PayloadDnD
        destino: string
    } | null>(null)
    const origen = useRef<{ cita: CitaMes; x: number; y: number } | null>(null)
    const moviendo = useRef(false)
    const [forma, setForma] = useState<FormaMes>(leerFormaMes)

    function elegirForma(v: FormaMes) {
        setForma(v)
        recordarFormaMes(v)
    }

    const porDia = new Map<string, CitaMes[]>()
    for (const c of citas) {
        const lista = porDia.get(c.dia)
        if (lista) lista.push(c)
        else porDia.set(c.dia, [c])
    }
    for (const lista of porDia.values()) lista.sort((a, b) => a.inicio.localeCompare(b.inicio))

    const cerrado = (d: string) => diaCerrado?.(d) === true
    const tieneCitas = (d: string) => (porDia.get(d)?.length ?? 0) > 0
    const cols = columnasMesQueSeVen(celdas, cerrado, tieneCitas, ocultarCerrados)
    const filas = filasMesQueSeVen(celdas, cols, cerrado, tieneCitas, ocultarCerrados)
    const celdasVisibles = filas.flatMap((fila) => cols.map((col) => celdas[fila * 7 + col] as string))
    const grid = GRID_COLS[cols.length] ?? 'grid-cols-7'

    function aplicarMismaHora() {
        if (!menu) return
        alProponer?.(propuestaAlCambiarDeDia(menu.payload, menu.destino))
        setMenu(null)
        setSobre(null)
    }

    function alPointerDown(e: PointerEvent<HTMLDivElement>, c: CitaMes) {
        if (e.button !== 0) return
        e.stopPropagation()
        origen.current = { cita: c, x: e.clientX, y: e.clientY }
        moviendo.current = false
        e.currentTarget.setPointerCapture(e.pointerId)
    }

    function alPointerMove(e: PointerEvent<HTMLDivElement>) {
        const o = origen.current
        if (!o) return
        if (!moviendo.current) {
            if (!superaUmbralArrastre(e.clientX - o.x, e.clientY - o.y)) return
            moviendo.current = true
        }
        const destino = diaBajoPuntero(e.clientX, e.clientY)
        setSobre(destino)
        setArrastre({ cita: o.cita, x: e.clientX, y: e.clientY })
    }

    function alPointerUp(e: PointerEvent<HTMLDivElement>) {
        const o = origen.current
        origen.current = null
        try {
            e.currentTarget.releasePointerCapture(e.pointerId)
        } catch {
            /* ya no la tenía */
        }
        if (!o) return
        e.stopPropagation()
        if (!moviendo.current) {
            setDetalle(o.cita)
            return
        }
        const destino = diaBajoPuntero(e.clientX, e.clientY)
        setArrastre(null)
        moviendo.current = false
        if (!destino) {
            setSobre(null)
            return
        }
        setSobre(destino)
        setMenu({ payload: payloadDe(o.cita), destino, x: e.clientX, y: e.clientY })
    }

    if (celdasVisibles.length === 0) {
        return (
            <p className="rounded-lg border border-neutral-800 bg-neutral-900/40 px-4 py-8 text-center text-sm text-neutral-400">
                Este mes el centro está cerrado.
            </p>
        )
    }

    const diasLista = diasDeListaMes(celdasVisibles, porDia)

    return (
        <>
            <div
                className="mb-2 flex md:hidden"
                role="group"
                aria-label="Cómo se ve el mes"
            >
                {(
                    [
                        ['lista', 'Lista'],
                        ['cuadricula', 'Cuadrícula'],
                    ] as const
                ).map(([v, et]) => (
                    <button
                        key={v}
                        type="button"
                        data-testid={`forma-mes-${v}`}
                        aria-pressed={forma === v}
                        onClick={() => elegirForma(v)}
                        className={
                            'min-h-11 px-3 text-sm first:rounded-l-md last:rounded-r-md ' +
                            (forma === v
                                ? 'bg-neutral-800 text-neutral-100'
                                : 'border border-neutral-800 text-neutral-500')
                        }
                    >
                        {et}
                    </button>
                ))}
            </div>

            <ol
                data-testid="mes-lista"
                className={cn('space-y-4', forma === 'lista' ? 'md:hidden' : 'hidden')}
            >
                {diasLista.length === 0 ? (
                    <li className="rounded-lg border border-neutral-800 bg-neutral-900/40 px-4 py-8 text-center text-sm text-neutral-400">
                        Ninguna cita este mes.
                    </li>
                ) : (
                    diasLista.map((d) => (
                        <li key={d} className="space-y-1.5">
                            <button
                                type="button"
                                onClick={() => alElegirDia(d)}
                                className="flex min-h-11 w-full items-center justify-between text-left text-sm capitalize text-neutral-200"
                            >
                                <span>{rotuloDiaLista(d)}</span>
                                {cerrado(d) && (
                                    <span className="text-[11px] uppercase tracking-wide text-neutral-500">
                                        cerrado
                                    </span>
                                )}
                            </button>
                            <ul className="space-y-1.5">
                                {(porDia.get(d) ?? []).map((c) => (
                                    <li key={c.id}>
                                        <button
                                            type="button"
                                            onClick={() => setDetalle(c)}
                                            className="flex min-h-11 w-full flex-col items-start gap-0.5 rounded-md border border-neutral-800 border-l-2 px-3 py-2 text-left"
                                            style={{ borderLeftColor: colorDe(c.profesionalId) }}
                                        >
                                            <span className="text-sm text-neutral-100">
                                                <span className="tabular-nums">{c.inicio}</span>
                                                {' · '}
                                                {rotuloCliente(c) || nombreServ(c.servicioId)}
                                            </span>
                                            <span className="text-xs text-neutral-500">
                                                {nombreServ(c.servicioId)}
                                                {c.profesionalId !== null &&
                                                    ` · ${nombreProf(c.profesionalId)}`}
                                            </span>
                                        </button>
                                    </li>
                                ))}
                            </ul>
                        </li>
                    ))
                )}
            </ol>

            <div
                className={cn(
                    'relative overflow-x-auto rounded-lg border border-neutral-800 bg-neutral-950/40',
                    forma === 'lista' && 'hidden md:block',
                )}
            >
            <div className={cn('grid border-b border-neutral-800', grid)}>
                {cols.map((i) => (
                    <div
                        key={DIAS_CABECERA[i]}
                        className="border-r border-neutral-900 px-2 py-1.5 text-[11px] font-medium text-neutral-400 last:border-r-0"
                    >
                        {DIAS_CABECERA[i]}
                    </div>
                ))}
            </div>

            <div className={cn('grid', grid)}>
                {celdasVisibles.map((d) => {
                    const delDia = porDia.get(d) ?? []
                    const abierta = expandido === d
                    const visibles = abierta ? delDia : delDia.slice(0, VISIBLES_POR_DIA)
                    const sobran = delDia.length - visibles.length
                    const deEsteMes = mismoMes(d, dia)
                    const esteCerrado = cerrado(d)
                    return (
                        <div
                            key={d}
                            data-dia={d}
                            role="button"
                            tabIndex={0}
                            onClick={() => {
                                if (menu || arrastre) return
                                if (clicCeldaMes(sobran > 0, abierta) === 'expandir') {
                                    setExpandido(d)
                                    return
                                }
                                alElegirDia(d)
                            }}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                    e.preventDefault()
                                    alElegirDia(d)
                                }
                            }}
                            title={esteCerrado ? `${d} · cerrado` : `Ver el ${d}`}
                            className={cn(
                                'flex min-h-28 cursor-pointer flex-col gap-0.5 border-b border-r border-neutral-900 p-1.5 text-left transition',
                                esteCerrado
                                    ? 'bg-neutral-800/70 hover:bg-neutral-800'
                                    : !deEsteMes
                                      ? 'bg-neutral-950/70 hover:bg-neutral-900/60'
                                      : 'hover:bg-neutral-900/60',
                                abierta && 'min-h-48',
                                sobre === d && 'bg-sky-950/40 ring-1 ring-inset ring-sky-500/40',
                            )}
                        >
                            <span
                                className={
                                    'text-[11px] tabular-nums ' +
                                    (d === hoy
                                        ? 'font-semibold text-sky-400'
                                        : esteCerrado
                                          ? 'text-neutral-500'
                                          : deEsteMes
                                            ? 'text-neutral-400'
                                            : 'text-neutral-700')
                                }
                            >
                                {Number(d.slice(8, 10))}
                                {esteCerrado && (
                                    <span className="ml-1 hidden text-[9px] font-normal uppercase tracking-wide text-neutral-500 md:inline">
                                        cerrado
                                    </span>
                                )}
                            </span>

                            {visibles.map((c) => {
                                const arrastrando = arrastre?.cita.id === c.id
                                return (
                                    <div
                                        key={c.id}
                                        role="button"
                                        tabIndex={0}
                                        onPointerDown={(e) => alPointerDown(e, c)}
                                        onPointerMove={alPointerMove}
                                        onPointerUp={alPointerUp}
                                        onPointerCancel={() => {
                                            origen.current = null
                                            moviendo.current = false
                                            setArrastre(null)
                                            setSobre(null)
                                        }}
                                        onKeyDown={(e) => {
                                            if (e.key === 'Enter' || e.key === ' ') {
                                                e.preventDefault()
                                                e.stopPropagation()
                                                setDetalle(c)
                                            }
                                        }}
                                        className={cn(
                                            'cursor-grab truncate rounded border-l-2 px-1 text-left text-[10px] leading-snug text-neutral-100 transition select-none',
                                            'hover:brightness-125 hover:ring-1 hover:ring-white/50',
                                            arrastrando && 'cursor-grabbing opacity-30',
                                            c.propuesta && 'border-dashed opacity-70',
                                        )}
                                        style={{
                                            borderLeftColor: colorDe(c.profesionalId),
                                            backgroundColor: `${colorDe(c.profesionalId)}26`,
                                            touchAction: 'none',
                                        }}
                                    >
                                        <span className="tabular-nums">{c.inicio}</span>{' '}
                                        {rotuloCliente(c) || nombreServ(c.servicioId)}
                                    </div>
                                )
                            })}

                            {sobran > 0 && (
                                <span className="px-1 text-[10px] text-neutral-500">+{sobran} más</span>
                            )}
                        </div>
                    )
                })}
            </div>
            </div>

            {arrastre && (
                <div
                    className="pointer-events-none fixed z-50 min-w-36 max-w-56 truncate rounded border-l-2 px-2 py-1 text-[11px] leading-snug text-neutral-50 shadow-lg shadow-black/60 ring-1 ring-white/30"
                    style={{
                        left: arrastre.x + 10,
                        top: arrastre.y + 10,
                        borderLeftColor: colorDe(arrastre.cita.profesionalId),
                        backgroundColor: `${colorDe(arrastre.cita.profesionalId)}cc`,
                    }}
                >
                    <span className="tabular-nums">{arrastre.cita.inicio}</span>{' '}
                    {rotuloCliente(arrastre.cita) || nombreServ(arrastre.cita.servicioId)}
                </div>
            )}

            {menu && (
                <MenuSoltarMes
                    x={menu.x}
                    y={menu.y}
                    alMismaHora={aplicarMismaHora}
                    alCambiarHora={() => {
                        const { payload, destino } = menu
                        setMenu(null)
                        setSobre(null)
                        window.setTimeout(() => setMoverHuecos({ payload, destino }), 0)
                    }}
                    alCerrar={() => {
                        setMenu(null)
                        setSobre(null)
                    }}
                />
            )}

            {moverHuecos && (
                <DialogoMoverCita
                    accountId={accountId}
                    citaId={moverHuecos.payload.id}
                    dia={moverHuecos.destino}
                    horaInicio={moverHuecos.payload.inicio}
                    minutos={moverHuecos.payload.minutos}
                    profesionalId={moverHuecos.payload.profesionalId}
                    alCerrar={() => setMoverHuecos(null)}
                />
            )}

            {detalle && (
                <DetalleCita
                    accountId={accountId}
                    dia={detalle.dia}
                    c={detalle}
                    nombreProf={nombreProf}
                    nombreServ={nombreServ}
                    color={colorDe(detalle.profesionalId)}
                    alCerrar={() => setDetalle(null)}
                />
            )}
        </>
    )
}
